import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiArrowDown } from 'react-icons/fi';

const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToProblems = () => {
    const element = document.getElementById('problems');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="pt-32 pb-20 bg-gradient-to-br from-gray-50 via-white to-accent-light/30 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Eyebrow */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-accent-magenta font-semibold text-lg tracking-wide uppercase"
            >
              We teach your child's brain how to learn.
            </motion.p>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl lg:text-6xl font-playfair font-bold text-gray-900 leading-tight"
            >
              <span className="gradient-text">Better Learning</span><br />
              <span className="gradient-text-animated">Starts Here.</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium"
            >
              Your kid can explain black holes, build incredible Lego masterpieces, or remember every Pokemon—but ask them to read a paragraph out loud and suddenly they're "can't."
            </motion.p>

            {/* Body Copy */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="space-y-4 text-lg text-gray-600 leading-relaxed"
            >
              <p>You're not imagining the disconnect.</p>
              <p>
                You've seen the frustration with reading, writing, and math. You've tried the tutors and the accommodations, but they only help on the next quiz, not for life.
              </p>
              <p>
                Those daily struggles are signs that their executive function system needs some specialized training.
              </p>
              <p className="font-semibold text-gray-800">
                We don't just teach content. We use cognitive therapy to rebuild the brain's learning architecture itself.
              </p>
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button onClick={scrollToContact} className="btn-gold text-lg px-10 py-5">
                Book Your Breakthrough Appointment
              </button>
              <button 
                onClick={scrollToProblems} 
                className="btn-secondary flex items-center justify-center gap-2"
              >
                Learn More <SafeIcon icon={FiArrowDown} className="h-5 w-5 animate-bounce" />
              </button>
            </motion.div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Children learning together with confidence and joy"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
              {/* Floating Stats */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="absolute -top-6 -left-6 bg-white p-4 rounded-xl shadow-lg"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent-magenta">25-80%</div>
                  <div className="text-sm text-gray-600">Reading Improvement</div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.2, duration: 0.5 }}
                className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-lg"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent-gold">90%</div>
                  <div className="text-sm text-gray-600">Stress Reduction</div>
                </div>
              </motion.div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -z-10 top-10 right-10 w-72 h-72 bg-accent-purple/20 rounded-full blur-3xl"></div>
            <div className="absolute -z-10 bottom-10 left-10 w-64 h-64 bg-accent-yellow/20 rounded-full blur-3xl"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;